import { useForm, useWatch, Controller, type Control, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { WidgetTranslationDto } from '@/Domain/api/WIdget/dto/WidgetTranslation.dto';
import { ContentEditor } from '@/Domain/components/Shared/ContentEditor/ContentEditor';
import { TiptapEditor } from '@/Domain/components/Shared/Editor/TiptapEditor/TiptapEditor';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';
import { useUpdateWidgetTranslation } from '@/Domain/hooks/Widget/useUpdateWidgetTranslation.hook';
import { cn } from '@/Domain/utils/cn';
import { buildPrimaryFormValues } from '@/UI/Website/TextContent/primaryDefaults';
import { toFormValues } from '@/UI/Website/TextContent/translationFormValues';
import { getTranslationStatus } from '@/UI/Website/TextContent/translationStatus';

const STARTER_MAX_LENGTH = 100;
const NOOP = () => {};

type TextKey = Exclude<
  keyof WidgetTranslationDto,
  'language' | 'country' | 'conversation_starters'
>;
type FieldDef = {
  key: TextKey;
  kind: 'input' | 'rich';
  label: string;
  maxLength?: number;
  noBubble?: boolean;
};

const TEXT_BEFORE_STARTERS: FieldDef[] = [
  { key: 'header', kind: 'input', label: 'header.label' },
  { key: 'launcher_label', kind: 'input', label: 'launcher_label.label', maxLength: 25 },
  { key: 'welcome_message', kind: 'rich', label: 'welcome_message.label' },
];

const TEXT_AFTER_STARTERS: FieldDef[] = [
  { key: 'input_placeholder', kind: 'input', label: 'input_placeholder.label' },
  {
    key: 'background_disclaimer_text',
    kind: 'rich',
    label: 'background_disclaimer_text.label',
    noBubble: true,
  },
];

const PRECHAT_INPUTS: FieldDef[] = [
  { key: 'prechat_header_text', kind: 'input', label: 'prechat_header_text.label' },
  { key: 'prechat_button_text', kind: 'input', label: 'prechat_button_text.label' },
];

// Render rich (HTML) source values as plain text — the source column is a static
// reference, not an editor. DOMParser parses into an inert document, so tenant HTML
// can't execute (innerHTML on a live-document node would run e.g. <img onerror>).
const htmlToText = (value: string): string => {
  if (!value) return '';
  if (typeof DOMParser === 'undefined' || value.indexOf('<') === -1) return value;
  const parsed = new DOMParser().parseFromString(value, 'text/html');
  return (parsed.body.textContent ?? '').trim();
};

const ColumnHeader = ({ active, children }: { active?: boolean; children: React.ReactNode }) => (
  <div className="border-b border-border pb-2">
    <span
      className={cn('text-sm font-medium', active ? 'text-foreground' : 'text-foreground-subtle')}
    >
      {children}
    </span>
  </div>
);

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="pt-4 lg:col-span-2">
    <span className="text-sm font-medium text-foreground">{children}</span>
  </div>
);

// Static, non-editable reference value for the Default (source) column.
const SourceValue = ({ value }: { value: string }) => (
  <div className="flex min-h-[34px] items-center whitespace-pre-wrap break-words py-1.5 text-sm text-muted-foreground">
    {value || <span className="text-foreground-subtle">-</span>}
  </div>
);

const TargetCell = ({
  def,
  control,
  fallback,
  isGenerating,
}: {
  def: FieldDef;
  control: Control<WidgetTranslationDto>;
  fallback: string;
  isGenerating?: boolean;
}) =>
  isGenerating ? (
    <Skeleton
      className={def.kind === 'rich' ? 'h-[88px] w-full' : 'h-[32px] w-full'}
      data-testid={`target-skeleton-${def.key}`}
    />
  ) : (
    <Controller
      control={control}
      name={def.key}
      render={({ field }) =>
        def.kind === 'rich' ? (
          <TiptapEditor
            value={field.value ?? ''}
            onChange={field.onChange}
            placeholder={fallback}
            enableBubbleMenu={!def.noBubble}
          />
        ) : (
          <Input
            size="sm"
            {...field}
            value={field.value ?? ''}
            maxLength={def.maxLength}
            placeholder={fallback}
          />
        )
      }
    />
  );

const FieldRow = ({
  def,
  sourceValue,
  control,
  t,
  isGenerating,
}: {
  def: FieldDef;
  sourceValue: string;
  control: Control<WidgetTranslationDto>;
  t: (key: string) => string;
  isGenerating?: boolean;
}) => (
  <>
    <FieldLabel>{t(def.label)}</FieldLabel>
    <div data-testid={`source-${def.key}`}>
      <SourceValue value={sourceValue} />
    </div>
    <div data-testid={`target-${def.key}`}>
      <TargetCell def={def} control={control} fallback={sourceValue} isGenerating={isGenerating} />
    </div>
  </>
);

// One target input per primary conversation starter, index-aligned. Empty target
// falls back to the matching default starter (shown as the placeholder).
const StartersRows = ({
  primaryStarters,
  form,
  t,
  isGenerating,
}: {
  primaryStarters: string[];
  form: UseFormReturn<WidgetTranslationDto>;
  t: (key: string) => string;
  isGenerating?: boolean;
}) => {
  const starters = useWatch({ control: form.control, name: 'conversation_starters' }) ?? [];
  const setStarter = (index: number, value: string) => {
    const next = primaryStarters.map((_, i) => (i === index ? value : (starters[i] ?? '')));
    form.setValue('conversation_starters', next, { shouldDirty: true });
  };

  return (
    <>
      <FieldLabel>{t('conversation_starters.label')}</FieldLabel>
      {primaryStarters.map((primary, index) => (
        <div key={index} className="contents" data-testid={`starter-row-${index}`}>
          <div data-testid={`source-starter-${index}`}>
            <SourceValue value={primary} />
          </div>
          <div data-testid={`target-starter-${index}`}>
            {isGenerating ? (
              <Skeleton
                className="h-[32px] w-full"
                data-testid={`target-starter-skeleton-${index}`}
              />
            ) : (
              <Input
                size="sm"
                value={starters[index] ?? ''}
                maxLength={STARTER_MAX_LENGTH}
                placeholder={primary}
                onChange={(event) => setStarter(index, event.target.value)}
                onBlur={(event) => {
                  const trimmed = event.target.value.trim();
                  if (trimmed !== event.target.value) setStarter(index, trimmed);
                }}
              />
            )}
          </div>
        </div>
      ))}
    </>
  );
};

type Props = {
  data: WidgetSettingsDto;
  language: string;
  country: string;
  translations: WidgetTranslationDto[];
  sourceLabel: string;
  targetLabel: string;
  isGenerating?: boolean;
};

export const TranslationEditor = ({
  data,
  language,
  country,
  translations,
  sourceLabel,
  targetLabel,
  isGenerating,
}: Props) => {
  const { t } = useTranslation(['widget', 'common', 'website']);
  const sourceValues = buildPrimaryFormValues(data, t);
  const primaryStarters = ((sourceValues.conversation_starters as string[]) ?? []).filter((s) =>
    s.trim(),
  );

  const row = translations.find((item) => item.language === language && item.country === country);
  const form = useForm<WidgetTranslationDto>({ values: toFormValues(row, language, country) });
  useUpdateWidgetTranslation({
    form,
    language,
    country,
    // Use the SAME filtered starter list the editor renders/writes against, so the
    // preview resolves target[index] on the identical base (a non-trailing blank in
    // the primary must not shift a translation to the wrong slot in the preview).
    fallbacks: {
      ...sourceValues,
      conversation_starters: primaryStarters,
    } as unknown as Record<string, unknown>,
  });

  const status = getTranslationStatus(data, row);

  const sourceText = (def: FieldDef): string => {
    const raw = (sourceValues[def.key as keyof WidgetSettingsDto] as string) ?? '';
    return def.kind === 'rich' ? htmlToText(raw) : raw;
  };

  const renderRows = (fields: FieldDef[]) =>
    fields.map((def) => (
      <FieldRow
        key={def.key}
        def={def}
        sourceValue={sourceText(def)}
        control={form.control}
        t={t}
        isGenerating={isGenerating}
      />
    ));

  const messageFallback = sourceValues.prechat_message_text as string;

  return (
    <form data-testid="translation-editor">
      <div className="grid grid-cols-1 items-start gap-x-8 gap-y-2 lg:grid-cols-2">
        <ColumnHeader>{sourceLabel}</ColumnHeader>
        <ColumnHeader active>
          <span className="flex items-center gap-2">
            {targetLabel}
            {status.total > 0 && (
              <span
                data-testid="editor-progress"
                className="text-xs font-normal text-foreground-subtle"
              >
                {status.filled}/{status.total}
              </span>
            )}
          </span>
        </ColumnHeader>

        {renderRows(TEXT_BEFORE_STARTERS)}

        {primaryStarters.length > 0 && (
          <StartersRows
            primaryStarters={primaryStarters}
            form={form}
            t={t}
            isGenerating={isGenerating}
          />
        )}

        {renderRows(TEXT_AFTER_STARTERS)}

        {/* Pre-chat fields only apply when the pre-chat screen is enabled — otherwise
            the visitor never sees them, so they shouldn't be shown or counted. */}
        {data.prechat_active && (
          <>
            <Separator className="mt-4 lg:col-span-2" />
            <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-foreground-subtle lg:col-span-2">
              {t('widget:prechat')}
            </p>

            {renderRows(PRECHAT_INPUTS)}

            {/* Pre-chat message: full width. The editor has a toolbar, so a side-by-side
                row would never line up with the static source. Default preview on top,
                editable translation below. */}
            <div className="mt-2 flex flex-col gap-3 lg:col-span-2">
              <span className="text-sm font-medium text-foreground">
                {t('prechat_message_text.label')}
              </span>
              <div
                data-testid="source-prechat_message_text"
                className="rounded-xl border border-border bg-muted/40 p-3"
              >
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
                  {sourceLabel}
                </p>
                <ContentEditor value={messageFallback} onChange={NOOP} editable={false} />
              </div>
              <div data-testid="target-prechat_message_text">
                {isGenerating ? (
                  <Skeleton
                    className="h-[168px] w-full"
                    data-testid="target-skeleton-prechat_message_text"
                  />
                ) : (
                  <Controller
                    control={form.control}
                    name="prechat_message_text"
                    render={({ field }) => (
                      <ContentEditor value={field.value ?? ''} onChange={field.onChange} />
                    )}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </form>
  );
};
